/**
 * The looping backdrop behind the sign-in screen.
 *
 * Three things decide what this component renders, and none of them is taste:
 *
 * **The supplied footage is the product story.** It remains full-bleed and
 * unobscured; readability support belongs at the text and real-form edges,
 * never in a second central panel laid over the footage.
 *
 * **Reduced motion is a preference, not a hint.** A decorative 42-second loop
 * is exactly what `prefers-reduced-motion: reduce` is about, so that setting
 * gets the poster frame and no video element at all - nothing decoded, nothing
 * moving.
 *
 * **The backdrop may never cost anyone a login.** It is `aria-hidden`, it takes
 * no focus, and any failure - decode error, missing file, a `play()` the
 * browser rejects - leaves the poster and the flat brand background behind it.
 * The form is a sibling, not a child.
 *
 * The supplied asset has no baked-in interface. It is requested once,
 * preloads metadata rather than the full clip, and remains a single source
 * for all seven timed stories. A restrained edge gradient supports the story
 * and form without covering the scene.
 */

import { memo, useEffect, useRef, useState } from "react";
import { storyIndexForTime } from "./loginStories";

// Not under /media: that prefix is proxied to the API in development, so an
// asset placed there is requested from the backend and 404s. Discovered by
// screenshotting the real page, where the poster came back broken.
const VIDEO_SRC = "/brand/auth-backdrop.mp4";
const POSTER_SRC = "/brand/auth-backdrop.jpg";

/** Below this the video is dropped: the phone sign-in screen is a form, not a stage. */
const SMALL_VIEWPORT = "(max-width: 720px)";
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/** Subscribes to a media query, defaulting to "not matching" where matchMedia is absent. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function AuthBackdropInner({onSceneChange}:{onSceneChange?:(scene:number)=>void}) {
  const reducedMotion = useMediaQuery(REDUCED_MOTION);
  const smallViewport = useMediaQuery(SMALL_VIEWPORT);
  const [failed, setFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const showVideo = !reducedMotion && !smallViewport && !failed;

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    // React applies `muted` as a property only, and Safari decides whether a
    // video may autoplay by looking at the attribute. Setting both is the
    // difference between a loop that plays on iOS and a poster that does not.
    element.muted = true;
    element.setAttribute("muted", "");
    // Autoplay is a request, not a guarantee, even muted. If the browser
    // refuses, the poster stays and nothing else changes.
    const attempt = element.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => setFailed(true));
    }
  }, [showVideo]);

  return (
    <div className="auth-backdrop" aria-hidden="true">
      {showVideo ? (
        <video
          ref={videoRef}
          className="auth-backdrop__video"
          src={VIDEO_SRC}
          poster={POSTER_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          tabIndex={-1}
          onError={() => setFailed(true)}
          onTimeUpdate={(event)=>onSceneChange?.(storyIndexForTime(event.currentTarget.currentTime,event.currentTarget.duration))}
        />
      ) : posterFailed ? (
        // Nothing to show, and that is a complete state: the flat brand
        // background behind this layer is a deliberate look, not an error.
        null
      ) : (
        <img
          className="auth-backdrop__video"
          src={POSTER_SRC}
          alt=""
          draggable={false}
          onError={() => setPosterFailed(true)}
        />
      )}
    </div>
  );
}

/**
 * Memoised because the sign-in form re-renders on every keystroke, and a
 * remounted `<video>` restarts the 42-second loop from zero. The component
 * receives a stable callback from the gate, so this remains a real barrier.
 */
export const AuthBackdrop = memo(AuthBackdropInner);
